import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingBar from "../LoadingBar";

import useLiffAuth from "../../hooks/useLiff";
import { useLiffProfile } from "../../context/LiffProfileContext";

const REDIRECT_BACK_PREFIX = ["/game"];

export const withAuthRedirect = (WrappedComponent) => {
  return (props) => {
    const { loggedIn, liffReady, liff } = useLiffAuth();
    const { authToken, userId, setProfile } = useLiffProfile();

    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [bigpoint, setBigpoint] = useState(null);
    const [isGuest, setIsGuest] = useState(false);
    const [retry, setRetry] = useState(0);
    const [hasRedirected, setHasRedirected] = useState(false);

    /* ---------------------------
     * 1) Sync LIFF token
     * --------------------------- */
    useEffect(() => {
      if (!liffReady) return;

      const sync = async () => {
        try {
          if (!loggedIn && liff?.getAccessToken()) {
            const p = await liff.getProfile();
            setProfile({
              authToken: liff.getAccessToken(),
              userId: p.userId,
              profile: p,
            });
          }
        } catch (e) {
          console.warn("LIFF sync failed", e);
        }
      };

      sync();
    }, [liffReady, loggedIn]);

    /* ---------------------------
     * 2) Fetch BigPoint
     * --------------------------- */
    useEffect(() => {
      if (!liffReady || !loggedIn) return;
      if (!authToken || !userId) return;
      if (retry > 3) {
        setLoading(false);
        // Clear corrupted data
        localStorage.removeItem("authToken");
        localStorage.removeItem("bigCardNumber");

        //navigate("/login", { replace: true });
        return;
      }

      const fetchBigPoint = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/users/current`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
                "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
                "x-user-id": userId,
                "Accept-Language": i18n.language.toUpperCase(),
              },
            },
          );

          const data = res.data?.data;

          // ✅ Guest → เคลียร์ BigPoint และจบ flow
          if (data?.isGuest) {
            localStorage.removeItem("bigCardNumber");
            setBigpoint(null);
            setIsGuest(true);
            setLoading(false);
            return; // ⛔ สำคัญมาก
          }

          // ✅ Member
          if (data?.bigCardNumber) {
            localStorage.setItem("bigCardNumber", data.bigCardNumber);
            setBigpoint(data.bigCardNumber);
            setIsGuest(false);
            setLoading(false);
            return;
          }

          // ❌ ไม่เข้าเงื่อนไข → retry
          setTimeout(() => setRetry((r) => r + 1), 800);
        } catch (err) {
          setTimeout(() => setRetry((r) => r + 1), 800);
        }
      };

      fetchBigPoint();
    }, [liffReady, loggedIn, authToken, userId, retry]);

    /* ---------------------------
     * 3) Auto Redirect (member only)
     * --------------------------- */
    const current = location.pathname;
    const shouldRedirectBack = REDIRECT_BACK_PREFIX.some((prefix) =>
      current.startsWith(prefix),
    );

    useEffect(() => {
      if (!loggedIn) return;
      if (loading) return;
      if (isGuest) return; // ⛔ guest ห้าม redirect
      if (!bigpoint) return;
      if (hasRedirected) return;

      setHasRedirected(true);

      if (shouldRedirectBack) {
        const from = location.state?.from || "/";
        // navigate(from, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }, [
      loggedIn,
      loading,
      bigpoint,
      isGuest,
      hasRedirected,
      shouldRedirectBack,
    ]);

    /* ---------------------------
     * 4) Render
     * --------------------------- */
    if (!loggedIn || loading) {
      return <LoadingBar loading />;
    }

    return <WrappedComponent {...props} />;
  };
};

export const withAuthBigPointRedirect = (WrappedComponent) => {
  return (props) => {
    const { i18n } = useTranslation();
    const { loggedIn, liffReady, liff, isRedirecting } = useLiffAuth(); // ⭐ เพิ่ม isRedirecting
    const { authToken, userId, setProfile } = useLiffProfile();

    const [bigpoint, setBigpoint] = useState(null);
    const [isGuest, setIsGuest] = useState(false);
    const [loading, setLoading] = useState(true);
    const [retry, setRetry] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const isMuteluPath = location.pathname.startsWith("/game/mutelu");
    const isBtsPath = location.pathname.startsWith("/game/bts");

    /** 1️⃣ Auto-sync token & user profile จาก LIFF */
    useEffect(() => {
      if (!liffReady || isRedirecting) return; // ⭐ รอให้ redirect เสร็จก่อน

      const autoSync = async () => {
        try {
          if (liff?.getAccessToken() && (!authToken || !userId)) {
            const profile = await liff.getProfile();
            const token = liff.getAccessToken();
            setProfile({ authToken: token, userId: profile.userId, profile });
          }
        } catch (err) {
          console.warn("⚠️ AutoSync LIFF failed:", err);
        }
      };
      autoSync();
    }, [liffReady, loggedIn, isRedirecting, authToken, userId]); // ⭐ เพิ่ม dependency

    /** 2️⃣ Fetch BigPoint */
    useEffect(() => {
      if (!liffReady || !loggedIn || isRedirecting) return; // ⭐ รอให้ redirect เสร็จก่อน
      if (!authToken || !userId) return;

      if (isMuteluPath || isBtsPath) {
        setIsGuest(false);
        setBigpoint(localStorage.getItem("bigCardNumber") || "BTS_AUTH_OK");
        setLoading(false);
        return;
      }

      if (retry > 3) {
        console.warn("❌ BigPoint fetch timeout");
        // Clear corrupted data
        localStorage.removeItem("authToken");
        localStorage.removeItem("bigCardNumber");
        if (!isMuteluPath) {
          navigate("/login", { replace: true });
        }
        return;
      }

      const fetchBigPoint = async () => {
        try {
          const headers = {
            Authorization: `Bearer ${authToken}`,
            "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
            "x-user-id": userId,
            "Accept-Language": i18n.language.toUpperCase(),
          };

          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/users/current`,
            { headers },
          );

          const data = res.data?.data || {};

          // ⭐ จัดการ Guest
          if (data?.isGuest) {
            console.warn("⚠️ Guest user - no BigPoint required");
            localStorage.removeItem("bigCardNumber");
            setBigpoint(null);
            setIsGuest(true);
            setLoading(false);
            return;
          }

          // ⭐ Member with BigPoint
          if (data?.bigCardNumber) {
            localStorage.setItem("bigCardNumber", data.bigCardNumber);
            setBigpoint(data.bigCardNumber);
            setIsGuest(false);
            setLoading(false);
            return;
          }

          // ⭐ Retry
          console.warn("⚠️ No BigPoint and not Guest, retrying...");
          setTimeout(() => setRetry((n) => n + 1), 1000);
        } catch (err) {
          console.error("❌ fetchBigPoint error:", err);
          if (err.response?.status === 401 || err.response?.status === 403) {
            console.warn("🔄 Token expired - clearing and re-authenticating");

            // Clear old data
            localStorage.removeItem("authToken");
            localStorage.removeItem("bigCardNumber");

            // Force re-login
            if (liff.isLoggedIn()) {
              liff.logout();
            }

            // Reload เพื่อเริ่ม auth flow ใหม่
            window.location.reload();
            return;
          }
          setTimeout(() => setRetry((n) => n + 1), 1000);
        }
      };

      fetchBigPoint();
    }, [liffReady, loggedIn, authToken, userId, retry, isRedirecting, isMuteluPath]); // ⭐ เพิ่ม dependency

    /** 3️⃣ แสดง Loading ตอนรอ */
    if (isRedirecting || loading || !liffReady || !authToken || !userId) {
      // ⭐ เพิ่มเช็ค isRedirecting
      return (
        <div className="flex flex-col items-center justify-center min-h-screen text-center">
          <LoadingBar loading={true} />
          {isRedirecting && (
            <p className="text-gray-500 text-sm mt-4">กำลังเปลี่ยนเส้นทาง...</p>
          )}
        </div>
      );
    }

    if (isMuteluPath || isBtsPath) {
      return <WrappedComponent {...props} />;
    }

    /** 4️⃣ Guest users */
    if (isGuest) {
      navigate("/login", { replace: true });
      return (
        <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md">
            <p className="text-gray-700 text-sm mb-4">
              คุณกำลังใช้งานในโหมดผู้เยี่ยมชม
              <br />
              ลงทะเบียนสมาชิก Big Point เพื่อรับสิทธิประโยชน์เต็มรูปแบบ
            </p>
            <button
              className="bg-green-500 text-white px-5 py-2 rounded-lg hover:bg-green-600"
              onClick={() =>
                navigate("/register", {
                  replace: true,
                  state: { from: location.pathname },
                })
              }
            >
              ลงทะเบียนสมาชิก
            </button>
          </div>
        </div>
      );
    }

    /** 5️⃣ No BigPoint */
    if (!bigpoint) {
      navigate("/login", { replace: true });
      return (
        <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
          <p className="text-gray-700 text-sm mb-4">
            ไม่พบหมายเลข Big Card
            <br />
            กรุณาเข้าสู่ระบบอีกครั้ง
          </p>
          <button
            className="bg-blue-500 text-white px-5 py-2 rounded-lg hover:bg-blue-600"
            onClick={() => navigate("/login", { replace: true })}
          >
            กลับไปหน้าเข้าสู่ระบบ
          </button>
        </div>
      );
    }

    /** 6️⃣ Success - แสดงเนื้อหา */
    return <WrappedComponent {...props} />;
  };
};
